// apps/console/src/services/api/robot-alert-pending.ts
//
// Alerts this console has closed whose close the server has not confirmed yet.
//
// An operator's answer takes an alert off the screen on the CLICK, not when the server replies, which
// can take seconds on a cold start. Until it replies, the alert is still OPEN on the server, so every
// read that lands in between would put it straight back: the 5 s poll, a snapshot after a reconnect,
// the device's next live sighting. The store consults this map to refuse all three. It lives apart
// from the store only for the store's line budget; nothing but the store and
// `optimistic-alert-removal.ts` touches it.

import { overlayDefined } from './robot-alerts-merge'
import type { RobotAlertItem } from './robot-alerts-types'

/** The alert as last known, so a refused close can put back exactly what it took away. */
const pending = new Map<string, RobotAlertItem | undefined>()

export function isRemovalPending(alertId: string): boolean {
  return pending.has(alertId)
}

/** Hold `alertId` off every list until {@link takePendingRemoval}. */
export function startPendingRemoval(alertId: string, item: RobotAlertItem | undefined): void {
  pending.set(alertId, item)
}

/**
 * Keep a sighting that arrived while the close is in flight, instead of showing it. Returns whether
 * the alert was pending, so the caller knows to stop there.
 *
 * Kept rather than dropped: if the close is refused, the alert comes back with what the device said
 * in the meantime, not as it was when the operator clicked.
 */
export function holdPendingSighting(item: RobotAlertItem): boolean {
  if (!pending.has(item.alertId)) return false

  pending.set(item.alertId, overlayDefined(pending.get(item.alertId), item))

  return true
}

/** End the hold. `null` when nothing was pending — a removal elsewhere already settled it. */
export function takePendingRemoval(alertId: string): { item: RobotAlertItem | undefined } | null {
  if (!pending.has(alertId)) return null

  const item = pending.get(alertId)

  pending.delete(alertId)

  return { item }
}

/** A removal from another surface wins over one still waiting for its reply. */
export function dropPendingRemoval(alertId: string): void {
  pending.delete(alertId)
}

export function __resetPendingRemovalsForTests(): void {
  pending.clear()
}
